// SearchField — the list-toolbar search input (magnifier + clear-X), per
// COMPONENTS §Catalog. A native text input owns the keyboard contract →
// hand-rolled (ADR-028); the only additions are the decorative magnifier, a
// labelled clear button when there is text, and Escape-clears (the standard
// searchbox affordance).
//
// - Controlled (`value` + `onChange(string)`) — debouncing is the caller's
//   concern (the DataTable global filter is already controlled).
// - Toolbar search fields carry no visible label: the accessible name comes
//   from `ariaLabel` (defaults to the i18n'd placeholder).
// - Clearing (X or Escape) returns focus to the input so the user can retype
//   immediately; Escape only swallows the event when there was text to clear
//   (an empty field lets Escape bubble — e.g. to close a surrounding overlay).

import { useRef, type KeyboardEvent, type RefObject } from 'react';
import { icons } from './icons.js';
import { useTranslation } from 'react-i18next';

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Accessible name (no visible label on toolbar search). Default: the placeholder. */
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  /** The input's ref for a caller that focuses it or reads it (the picker's Enter). */
  inputRef?: RefObject<HTMLInputElement | null>;
  /** After the field's own keys (Escape clears a full field): a caller's Enter / Escape. */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

export function SearchField({
  value,
  onChange,
  placeholder,
  ariaLabel,
  disabled = false,
  className = '',
  inputRef: inputRefProp,
  onKeyDown,
}: SearchFieldProps) {
  const { t } = useTranslation('common');
  const ownRef = useRef<HTMLInputElement>(null);
  const inputRef = inputRefProp ?? ownRef;

  const effectivePlaceholder = placeholder ?? t('search.placeholder');

  const clear = () => {
    onChange('');
    inputRef.current?.focus();
  };

  return (
    <div
      className={
        // disabled grays the WHOLE frame (not just the inner input). field-frame
        // owns the border + hover + focus light-up + the 2.5rem field height
        // (DESIGN-TOKENS §Interaction states).
        'relative flex items-center rounded-md field-frame transition-colors ' +
        (disabled
          ? 'bg-[var(--color-bg-surface-alt)] cursor-not-allowed '
          : 'bg-[var(--color-bg-page)] ') +
        className
      }
    >
      <icons.search
        aria-hidden
        size={16}
        className="absolute start-3 text-[var(--color-text-secondary)] pointer-events-none"
      />
      <input
        ref={inputRef}
        type="text"
        role="searchbox"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && value !== '') {
            e.stopPropagation();
            clear();
            return;
          }
          // Consumer first — a caller that owns Enter (a picker's pick-on-Enter)
          // marks it with preventDefault and the field stays focused.
          onKeyDown?.(e);
          // Touch devices, unclaimed Enter: the Search softkey's job ends here —
          // results already filter live, so retract the keyboard (blur). Desktop
          // is untouched; matchMedia is feature-detected (jsdom has none).
          if (
            e.key === 'Enter' &&
            !e.defaultPrevented &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(pointer: coarse)').matches
          ) {
            e.currentTarget.blur();
          }
        }}
        placeholder={effectivePlaceholder}
        aria-label={ariaLabel ?? effectivePlaceholder}
        disabled={disabled}
        autoComplete="off"
        enterKeyHint="search"
        className={
          // pointer-coarse:text-base — iOS input-zoom guard (canonical note: Input.tsx).
          'w-full ps-9 pe-9 py-2 text-sm pointer-coarse:text-base bg-transparent outline-none border-0 ' +
          'text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)] ' +
          'disabled:text-[var(--color-text-disabled)] disabled:cursor-not-allowed'
        }
      />
      {value !== '' && !disabled && (
        <button
          type="button"
          aria-label={t('search.clear')}
          onClick={clear}
          className={
            'absolute end-1 p-1.5 min-w-[1.75rem] min-h-[1.75rem] inline-flex items-center justify-center rounded-md cursor-pointer ' +
            'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] ' +
            'outline-none focus-ring-inset'
          }
        >
          <icons.close aria-hidden size={14} />
        </button>
      )}
    </div>
  );
}
